<script setup lang="ts">
// Renders the toast queue (mount once in the app shell). Owns auto-dismiss timers.
import { computed, watch, onBeforeUnmount } from 'vue'
import { RouterLink } from 'vue-router'

import { injectUiLabels } from '@/shared/i18n'
import { Icon, type IconName } from '@/shared/ui/icon'
import { OverlayPortal } from '@/shared/ui/overlay'
import { Spinner } from '@/shared/ui/spinner'
import { useUiStore } from '@/shared/ui/store/uiStore'
import type { UiTone } from '@/shared/ui/types'

const store = useUiStore()
const labels = injectUiLabels()
const timers = new Map<number, ReturnType<typeof setTimeout>>()
const scheduledRevision = new Map<number, number>()
const dismissLabel = computed(() => labels.close)
const toneIcons: Record<UiTone, IconName> = {
  neutral: 'info',
  info: 'info',
  success: 'check',
  warning: 'info',
  danger: 'circle-alert',
}
const toneClasses: Record<UiTone, string> = {
  neutral: 'bg-toast-neutral-bg text-toast-neutral-fg',
  info: 'bg-toast-info-bg text-toast-info-fg',
  success: 'bg-toast-success-bg text-toast-success-fg',
  warning: 'bg-toast-warning-bg text-toast-warning-fg',
  danger: 'bg-toast-danger-bg text-toast-danger-fg',
}
const toastGroups = computed(() => [
  {
    placement: 'top-end' as const,
    toasts: store.toasts.filter((toast) => toast.placement === 'top-end'),
  },
  {
    placement: 'bottom-end' as const,
    toasts: store.toasts.filter((toast) => toast.placement === 'bottom-end'),
  },
])

function clearTimer(id: number): void {
  const timer = timers.get(id)
  if (timer) clearTimeout(timer)
  timers.delete(id)
  scheduledRevision.delete(id)
}

watch(
  () => store.toasts.map((toast) => `${toast.id}:${toast.revision}:${toast.timeout}`),
  () => {
    const activeIds = new Set(store.toasts.map((toast) => toast.id))
    for (const id of timers.keys()) {
      if (!activeIds.has(id)) clearTimer(id)
    }

    for (const toast of store.toasts) {
      if (scheduledRevision.get(toast.id) === toast.revision) continue
      clearTimer(toast.id)
      scheduledRevision.set(toast.id, toast.revision)
      if (toast.timeout <= 0) continue
      timers.set(
        toast.id,
        setTimeout(() => {
          store.dismissToast(toast.id)
          clearTimer(toast.id)
        }, toast.timeout),
      )
    }
  },
  { immediate: true },
)

onBeforeUnmount(() => {
  timers.forEach((t) => clearTimeout(t))
  timers.clear()
  scheduledRevision.clear()
})
</script>

<template>
  <OverlayPortal>
    <div
      v-for="group in toastGroups"
      :key="group.placement"
      class="ui-toast-host pointer-events-none fixed right-[0.625rem] z-toast flex w-[min(21.25rem,calc(100vw_-_1.25rem))] flex-col gap-xs"
      :class="[
        `ui-toast-host--${group.placement}`,
        group.placement === 'top-end' ? 'top-[0.625rem]' : 'bottom-[0.625rem]',
      ]"
      aria-live="polite"
    >
      <div
        v-for="toast in group.toasts"
        :key="toast.id"
        class="ui-toast pointer-events-auto flex min-h-[4.75rem] items-start gap-5 rounded-[0.3125rem] border-0 p-md font-sans shadow-toast"
        :class="[`ui-toast--${toast.tone}`, toneClasses[toast.tone]]"
        role="status"
        aria-atomic="true"
      >
        <Spinner
          v-if="toast.progress"
          class="ui-toast__progress mt-0.5 grid flex-none place-items-center text-current"
          size="sm"
          aria-hidden="true"
        />
        <span
          v-else
          class="ui-toast__tone-icon mt-0.5 grid flex-none place-items-center"
          aria-hidden="true"
        >
          <Icon :name="toneIcons[toast.tone]" :size="24" class="[--ui-icon-stroke-width:2]" />
        </span>
        <div class="ui-toast__content min-w-0 flex-1 pt-0.5">
          <p v-if="toast.title" class="ui-toast__title m-0 mb-2xs text-sm font-semibold leading-5">
            {{ toast.title }}
          </p>
          <p class="ui-toast__message m-0 text-xs font-medium leading-4 opacity-90">
            {{ toast.message }}
          </p>
          <a
            v-if="toast.action && typeof toast.action.to === 'string'"
            class="ui-toast__action mt-xs inline-flex font-semibold text-toast-link no-underline hover:underline"
            :href="toast.action.to"
            target="_blank"
            rel="noreferrer"
            @click="store.dismissToast(toast.id)"
          >
            {{ toast.action.label }}
          </a>
          <RouterLink
            v-else-if="toast.action"
            class="ui-toast__action mt-xs inline-flex font-semibold text-toast-link no-underline hover:underline"
            :to="toast.action.to"
            @click="store.dismissToast(toast.id)"
          >
            {{ toast.action.label }}
          </RouterLink>
        </div>
        <button
          v-if="toast.dismissible"
          type="button"
          class="ui-toast__close grid flex-none cursor-pointer place-items-center border-0 bg-transparent p-0.5 text-toast-close"
          :aria-label="dismissLabel"
          @click="store.dismissToast(toast.id)"
        >
          <Icon name="x" :size="20" aria-hidden="true" />
        </button>
      </div>
    </div>
  </OverlayPortal>
</template>
